import React, { useEffect, useState } from "react";
import Tabs from "../../../../components/elements/Tabs";
import BasicSettingsManageForm from "../../../../components/sections/front/manage-organization/BasicSettingsManageForm";
import Billing from "../../../../components/elements/tabContent/Billing";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { organizationDetails } from "../../../../services/organizationService";
import OrganisationDesign from "../../../../components/elements/tabContent/OrganisationDesign";
import PaymentSettings from "../payment/PaymentSettings";
import PrivacyPolicySettings from "../../../../components/sections/front/manage-organization/PrivacyPolicySettings";
import BannedEmailsSettings from "../../../../components/sections/front/manage-organization/BannedEmailsSettings";
import ContactPref from "../../../../components/sections/front/manage-organization/ContactPref";
import TaxSetting from "../transaction/taxSetting";
import { useSelector } from "react-redux";
import LoadingFallback from "../../../../components/elements/LoadingFallback";
import TeamAccess from "../../../../components/sections/front/team-access/TeamAccess";
import OrganizationReview from "../../../../components/sections/front/manage-organization/OrganizationReview";
import OrganizationFollower from "../../../../components/sections/front/manage-organization/OrganizationFollower";
import GlobalCheckoutForm from "../../../../components/elements/tabContent/CheckoutForm/GlobalCheckoutForm";
import { useTranslation } from "react-i18next";

const BasicSettings = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location?.search);
  const { sec: tabSec } = Object.fromEntries(params.entries());
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const { selectedOrganizationDetails } = useSelector(
    (state) => state?.organization
  );
  const { data: profileData } = useSelector((state) => state?.profile);
  const { organizer, organizationRefetch } = useOutletContext();

  const tabItems = [
    {
      label: t("Basic settings"),
      icon: "fa-regular fa-wrench",
      content: (
        <BasicSettingsManageForm
          data={organizer?.data}
          organizationDetailsRefetch={organizationRefetch}
        />
      ),
    },
    {
      label: t("Organization design"),
      icon: "fa-regular fa-pen-nib",
      content: (
        <OrganisationDesign
          data={organizer?.data}
          organizationDetailsRefetch={organizationRefetch}
        />
      ),
    },
    {
      key: "checkoutForm",
      label: t("Global checkout form"),
      icon: "fa-regular fa-clipboard-list",
      content: (
        <GlobalCheckoutForm organizationDetailsRefetch={organizationRefetch} />
      ),
    },
    {
      label: t("Checkout fees and tax"),
      icon: "fa-regular fa-calculator",
      content: (
        <TaxSetting
          data={organizer?.data}
          organizationDetailsRefetch={organizationRefetch}
        />
      ),
    },
    ...(organizer?.data?.userData?._id === profileData?._id
      ? [
          {
            key: "payment",
            label: t("Payment systems"),
            icon: "fa-regular fa-credit-card",
            content: (
              <PaymentSettings
                data={organizer?.data}
                organizationDetailsRefetch={organizationRefetch}
              />
            ),
          },
        ]
      : []),
    {
      label: t("Team access"),
      icon: "fa-regular fa-users",
      content: <TeamAccess />,
    },
    {
      label: t("Contact preferences"),
      icon: "fa-regular fa-user-gear",
      content: (
        <ContactPref
          data={organizer?.data}
          organizationDetailsRefetch={organizationRefetch}
        />
      ),
    },
    {
      label: t("Organization review"),
      icon: "fa-regular fa-clipboard-check",
      content: (
        <OrganizationReview
          data={organizer?.data}
          organizationDetailsRefetch={organizationRefetch}
        />
      ),
    },
    {
      label: t("Organization follower"),
      icon: "fa-regular fa-user-plus",
      content: (
        <OrganizationFollower
          data={organizer?.data}
          organizationDetailsRefetch={organizationRefetch}
        />
      ),
    },
    {
      label: t("Privacy policy"),
      icon: "fa-regular fa-shield",
      content: (
        <PrivacyPolicySettings
          data={organizer?.data}
          organizationDetailsRefetch={organizationRefetch}
        />
      ),
    },
    {
      label: t("Banned emails"),
      icon: "fa-regular fa-envelope",
      content: (
        <BannedEmailsSettings
          data={organizer?.data}
          organizationDetailsRefetch={organizationRefetch}
        />
      ),
    },
    // {
    //   label: "Billing",
    //   icon: "fa-regular fa-circle-dollar",
    //   content: <Billing />,
    // },
  ];

  useEffect(() => {
    if (tabSec) {
      const findTabIndex = tabItems?.findIndex((item) => item?.key === tabSec);
      if (findTabIndex !== -1) {
        setSelectedTabIndex(findTabIndex);
      }
    }
  }, [tabSec, setSelectedTabIndex, tabItems]);

  if (selectedOrganizationDetails?.loading) {
    return <LoadingFallback />;
  }

  return (
    <div className="w-full relative flex items-start">
      <Tabs
        items={tabItems}
        selectedIndex={selectedTabIndex}
        setSelectedIndex={(i) => {
          navigate(location?.pathname);
          setSelectedTabIndex(i);
        }}
        tabButtonClass="w-full text-deep-purple hover:bg-primary/10 font-medium rounded-md"
        tabClass={"font-normal  min-w-52"}
        tabsClass="bg-white w-full lg:w-72 xl:w-72 rounded-md shadow-sm px-2 md:px-4 py-4 lg:py-6 space-y-2 lg:h-[calc(100vh-8rem)] overflow-auto scrollbar w-full max-w-full scrollbar-1 flex-shrink-0 "
        contentClass="px-0 flex-shrink"
        type={"menu"}
        activeTabClass={"!bg-primary !text-white"}
        tablistsClass="lg:sticky lg:top-24 overflow-auto lg:overflow-visible w-full lg:w-auto"
        tabWrapperClass={"flex-col lg:flex-row"}
      />
    </div>
  );
};

export default BasicSettings;
